import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { NoiseAnalytics, NoiseFile } from "../api/types";
import { toast } from "./toast";
import { backdropClose } from "../lib/backdrop";

/**
 * Noise Analytics (superadmin, while the detector lives in the Lab): score the chosen files with a Lab model,
 * then review its flags on the project's Noise Analytics tab.
 *
 * Only proofed files can be scored: each flag's text quotes the words around the noise, and those come from
 * proofing. The others are named with the reason, so it's clear why they were left out.
 */

export const NOISE_BASE = "/api/modules/noise_lab/analytics";

export const scanning = (f: NoiseFile) => !!f.scan && (f.scan.status === "queued" || f.scan.status === "running");

export function ModelPicker({ data, value, onChange }: {
  data: NoiseAnalytics; value: string; onChange: (id: string) => void;
}) {
  return (
    <select className="input nad-model" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Model">
      {data.models.filter((m) => m.status !== "retired" || m.id === value).map((m) => (
        <option key={m.id} value={m.id}>{m.name}{m.status === "live" ? " (live)" : ""}</option>
      ))}
    </select>
  );
}

export async function startScans(projectId: string, chapterIds: string[], modelId: string) {
  const r = await api<{ queued: string[]; skipped: { name: string; reason: string }[] }>(`${NOISE_BASE}/${projectId}/scans`, {
    method: "POST", body: JSON.stringify({ chapter_ids: chapterIds, model_id: modelId }),
  });
  toast(r.queued.length
    ? `Scoring ${r.queued.length} file${r.queued.length === 1 ? "" : "s"}: each is ready to review as soon as it's done`
    : "Nothing to score: those files are already being scored", r.queued.length ? "success" : "info");
  return r;
}

export function NoiseAnalyticsDialog({ projectId, filenames, onClose }: {
  projectId: string; filenames: string[]; onClose: () => void;
}) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [modelId, setModelId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["noise", projectId],
    queryFn: () => api<NoiseAnalytics>(`${NOISE_BASE}/${projectId}`),
    staleTime: 0,
  });

  const picked = (data?.files ?? []).filter((f) => filenames.includes(f.name));
  const ready = picked.filter((f) => !f.why_not && !scanning(f));
  const again = ready.filter((f) => f.scan?.status === "done").length;
  const reasons = new Map<string, number>();
  for (const f of picked) {
    const why = f.why_not || (scanning(f) ? "already being scored" : "");
    if (why) reasons.set(why, (reasons.get(why) ?? 0) + 1);
  }
  const notFiles = filenames.length - picked.length;          // not a proofing file at all
  if (notFiles > 0) reasons.set("not proofed yet", (reasons.get("not proofed yet") ?? 0) + notFiles);
  const model = modelId ?? data?.default_model_id ?? "";

  async function start() {
    setBusy(true);
    try {
      await startScans(projectId, ready.map((f) => f.chapter_id), model);
      qc.invalidateQueries({ queryKey: ["noise", projectId] });
      onClose();
      nav(`/projects/${projectId}/noise`);
    } catch (e) {
      toast((e as Error).message || "Couldn't start the scoring", "error");
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" {...backdropClose(() => { if (!busy) onClose(); })}>
      <div className="modal-card nad" role="dialog" aria-modal="true" aria-labelledby="nad-title" onClick={(e) => e.stopPropagation()}>
        <h3 id="nad-title">Noise Analytics</h3>
        <p className="nid-lede">
          A Lab model listens to each file and flags the noises it hears. Each file is ready to review on the
          <b> Noise Analytics</b> tab as soon as it's scored, about 20 minutes per hour of audio, one file at a time.
        </p>
        {isLoading ? (
          <div className="nid-wait"><span className="spinner" style={{ width: 15, height: 15 }} /> Checking the files…</div>
        ) : error || !data ? (
          <p className="dfd-warn">Couldn't check the files. Close this and try again.</p>
        ) : !data.models.length ? (
          <p className="nid-none">No model yet: upload one on the Noise Lab's Models page.</p>
        ) : (
          <>
            <label className="nad-row">
              <span>Model</span>
              <ModelPicker data={data} value={model} onChange={setModelId} />
            </label>
            {ready.length > 0 ? (
              <p className="nad-count">
                <b>{ready.length}</b> file{ready.length === 1 ? "" : "s"} will be scored
                {again > 0 && <span className="muted"> ({again} again, replacing the flags you haven't reviewed)</span>}.
              </p>
            ) : (
              <p className="nid-none">None of these files can be scored right now.</p>
            )}
            {reasons.size > 0 && (
              <p className="nid-skipped">
                Left out:{" "}
                {[...reasons].map(([why, n], i) => (
                  <span key={why}>{i > 0 && " · "}<b>{n}</b> {why}</span>
                ))}
              </p>
            )}
          </>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" disabled={busy || !ready.length || !model} onClick={start}>
            {busy ? "Starting…" : ready.length ? `Score ${ready.length} file${ready.length === 1 ? "" : "s"}` : "Score"}
          </button>
        </div>
      </div>
    </div>
  );
}
